import { Award } from "lucide-react";
import { PARTNER_TIER_LABEL, type PartnerTier } from "@colab/shared";
import { cn } from "@/lib/utils";

// Public storage URL prefix for badges saved via apps/api/services/storage.ts::uploadTierBadge.
const BADGE_PUBLIC_PREFIX = `${
  import.meta.env.VITE_SUPABASE_URL ?? ""
}/storage/v1/object/public/content/`;

const TIER_STYLES: Record<PartnerTier, { bg: string; fg: string; ring: string }> = {
  bronze: { bg: "bg-tier-bronze/10", fg: "text-tier-bronze", ring: "ring-tier-bronze/30" },
  silver: { bg: "bg-tier-silver/10", fg: "text-tier-silver", ring: "ring-tier-silver/30" },
  gold: { bg: "bg-tier-gold/10", fg: "text-tier-gold", ring: "ring-tier-gold/40" },
  platinum: { bg: "bg-tier-platinum/10", fg: "text-tier-platinum", ring: "ring-tier-platinum/40" },
};

export type TierBadgeAssignment = {
  tierName: string;
  badgePath?: string | null;
  colorHex?: string | null;
  // When the assignment carries a legacy mapping we reuse the canonical
  // colour styles; otherwise we fall back to colorHex or a neutral chip.
  legacyTier?: PartnerTier | null;
};

type Props =
  | { tier: PartnerTier; className?: string }
  | { assignment: TierBadgeAssignment; className?: string };

// Two modes — see legacy components/shared/TierBadge.tsx for the original
// rationale. Mode (1) drives off the legacy tier enum; mode (2) renders the
// Session-5 native assignment shape with optional badge image + custom hex.
export function TierBadge(props: Props) {
  if ("assignment" in props) {
    const { assignment, className } = props;
    const badgeUrl = assignment.badgePath
      ? `${BADGE_PUBLIC_PREFIX}${assignment.badgePath}`
      : null;

    if (badgeUrl) {
      return (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full bg-muted/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ring-1 ring-border",
            className,
          )}
        >
          <img
            src={badgeUrl}
            alt={`${assignment.tierName} badge`}
            width={16}
            height={16}
            className="h-4 w-4 rounded-sm object-cover"
          />
          {assignment.tierName}
        </span>
      );
    }

    if (assignment.legacyTier) {
      const s = TIER_STYLES[assignment.legacyTier];
      return (
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ring-1",
            s.bg,
            s.fg,
            s.ring,
            className,
          )}
        >
          <Award className="h-3 w-3" />
          {assignment.tierName}
        </span>
      );
    }

    const style = assignment.colorHex
      ? {
          backgroundColor: `${assignment.colorHex}1a`,
          color: assignment.colorHex,
          borderColor: `${assignment.colorHex}40`,
        }
      : undefined;
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider",
          !style && "bg-muted/40 text-foreground/80",
          className,
        )}
        style={style}
      >
        <Award className="h-3 w-3" />
        {assignment.tierName}
      </span>
    );
  }

  const { tier, className } = props;
  const s = TIER_STYLES[tier];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ring-1",
        s.bg,
        s.fg,
        s.ring,
        className,
      )}
    >
      <Award className="h-3 w-3" />
      {PARTNER_TIER_LABEL[tier]}
    </span>
  );
}
